'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';
import { DropdownOption } from './Dropdown';
import DropdownSearch from './DropdownSearch';

export interface SearchInputOption {
  text: string;
  value: string;
}

export interface SearchInputLabels {
  label?: string;
  placeholder: string;
  searchPlaceholder: string;
  choseCustomText?: string;
  suggestCustomText?: string;
  description?: string;
  /** Shown while results are loading */
  searchingText: string;
  /** Shown before min search length is reached */
  emptyText: string;
  /** Shown when search yields no results */
  noResultsText: string;
  /** Inline error text shown when the backend search fails */
  technicalErrorText?: string;
}

export interface SearchInputProps<T extends SearchInputOption> extends SearchInputLabels {
  /** Results from the app's fetch hook (pre-mapped to have text + value) */
  items?: T[];
  isLoading?: boolean;
  isValidating?: boolean;
  searchError?: Error | null;

  /** Controlled search string — managed by the parent alongside the fetch hook */
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  minSearchLength?: number;

  /** Currently selected value */
  value?: string;
  /**
   * Fallback item used for rehydration when value is set but not found in items.
   * Typically a synthetic or previously-loaded item passed by the parent.
   */
  currentItem?: T;
  onChange: (value?: T) => void;
  disabled?: boolean;
  /** External validation error from the form */
  error?: string;
  allowCustom?: boolean;
  /**
   * Converts a custom (user-typed) text+value pair into the full item type T.
   * Required when allowCustom is true.
   */
  normalizeCustom?: (text: string, value: string) => T;

  /**
   * Render prop for the technical error modal.
   * The component controls when it is shown; the app controls what it looks like.
   *
   * @param open    Whether the modal should be visible
   * @param onClose Call this to dismiss the modal
   */
  renderErrorModal?: (open: boolean, onClose: () => void) => ReactNode;
  /**
   * When false, the currently selected item is not prepended to the search results.
   * Defaults to true (existing behaviour).
   */
  showSelectedInSearch?: boolean;
}

export function SearchInput<T extends SearchInputOption>({
  items = [],
  isLoading = false,
  isValidating = false,
  searchError,
  searchValue,
  onSearchValueChange,
  minSearchLength = 1,
  value,
  currentItem,
  onChange,
  disabled,
  error,
  allowCustom = false,
  normalizeCustom,
  showSelectedInSearch = true,
  label,
  placeholder,
  searchPlaceholder,
  choseCustomText,
  suggestCustomText,
  description,
  searchingText,
  emptyText,
  noResultsText,
  technicalErrorText,
  renderErrorModal,
}: SearchInputProps<T>) {
  const [selectedOption, setSelectedOption] = useState<T | undefined>(undefined);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const lastSearchValueErrorShownRef = useRef<string>('');

  // Sync search value when value is cleared externally (persistence/rehydration)
  useEffect(() => {
    if (!value) {
      onSearchValueChange('');
    }
    // Only react to value clearing — onSearchValueChange is stable from parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Rehydrate selectedOption when value arrives without a matching option yet
  useEffect(() => {
    if (!value) return;
    if (selectedOption && selectedOption.value === value) return;
    const found = items.find(item => item.value === value);
    if (found) {
      setSelectedOption(found);
    } else if (currentItem) {
      setSelectedOption(currentItem);
    }
    // currentItem intentionally omitted — we only re-run when value or items change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items]);

  const trimmedSearchValue = searchValue.trim();
  const isSearching = trimmedSearchValue.length >= minSearchLength;
  const loading = isSearching && (isLoading || isValidating);
  const hasBackendError = isSearching && !!searchError;

  // Trigger error modal once per unique search string that produces an error
  useEffect(() => {
    if (!hasBackendError) {
      lastSearchValueErrorShownRef.current = '';
      return;
    }
    if (lastSearchValueErrorShownRef.current === trimmedSearchValue) return;
    lastSearchValueErrorShownRef.current = trimmedSearchValue;
    setShowErrorModal(true);
  }, [hasBackendError, trimmedSearchValue]);

  function handleChange(newValue?: DropdownOption) {
    if (disabled) return;
    if (!newValue) {
      onChange(undefined);
      setSelectedOption(undefined);
      onSearchValueChange('');
      return;
    }
    // Try to find the full typed item in the current results
    const found = items.find(item => item.value === newValue.value);
    if (found) {
      onChange(found);
      setSelectedOption(found);
      return;
    }
    // Fall back to the currently selected option (e.g. prepended selection)
    if (selectedOption && selectedOption.value === newValue.value) {
      onChange(selectedOption);
      return;
    }
    // Handle custom (user-typed) values
    if (allowCustom && normalizeCustom) {
      const normalized = normalizeCustom(newValue.text, newValue.value);
      onChange(normalized);
      setSelectedOption(normalized);
    }
  }

  // Build options list — strip to DropdownOption shape for DropdownSearch
  const baseOptions = items.map(a => ({ text: a.text, value: a.value }));
  const selectedDropdown = selectedOption ? { text: selectedOption.text, value: selectedOption.value } : undefined;
  const hasSelectedInBase = selectedDropdown ? baseOptions.some(o => o.value === selectedDropdown.value) : false;
  const optionsWithSelected =
    showSelectedInSearch && selectedDropdown && !hasSelectedInBase ? [selectedDropdown, ...baseOptions] : baseOptions;
  const optionsToRender = isSearching ? optionsWithSelected : selectedDropdown ? [selectedDropdown] : [];

  const noResultsDisplayText = loading
    ? searchingText
    : trimmedSearchValue.length < minSearchLength
      ? emptyText
      : noResultsText;
  const inlineError = error ?? (hasBackendError ? technicalErrorText : undefined);

  return (
    <>
      <DropdownSearch
        label={label}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        choseCustomText={choseCustomText}
        suggestCustomText={suggestCustomText}
        noResultsText={noResultsDisplayText}
        isLoading={loading}
        description={description}
        options={optionsToRender}
        error={inlineError}
        onChange={handleChange}
        disabled={disabled}
        value={value}
        searchValue={searchValue}
        onSearchValueChange={onSearchValueChange}
        allowCustom={allowCustom}
        filterAgainstInput={false}
      />
      {renderErrorModal?.(showErrorModal, () => setShowErrorModal(false))}
    </>
  );
}
